import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BarChart3, ExternalLink } from 'lucide-react';

const ADMIN_PASSWORD = '729393';
const GA_PROPERTY_ID = 'G-CNS23K1QHF';
const GA_DASHBOARD_URL = 'https://analytics.google.com/analytics/web/#/p529124502/reports/intelligenthome';
const LOOKER_STUDIO_EMBED_URL = 'https://lookerstudio.google.com/embed/reporting/a2fb904a-0e0b-40b8-acc1-c66cf4a90d6f/page/XnlsF';

const MAX_ATTEMPTS = 5;
const LOCKOUT_MS = 10 * 60 * 1000; // 10分
const STORAGE_KEY = 'admin_lockout';

interface LockoutState {
  attempts: number;
  lockedUntil: number | null;
}

function getLockout(): LockoutState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { attempts: 0, lockedUntil: null };
  } catch {
    return { attempts: 0, lockedUntil: null };
  }
}

function saveLockout(state: LockoutState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

const AdminLoginForm = ({ onLogin }: { onLogin: () => void }) => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLocked, setIsLocked] = useState(false);
  const [lockRemaining, setLockRemaining] = useState(0);
  const [isWaiting, setIsWaiting] = useState(false);

  useEffect(() => {
    const state = getLockout();
    if (state.lockedUntil && Date.now() < state.lockedUntil) {
      setIsLocked(true);
      setLockRemaining(Math.ceil((state.lockedUntil - Date.now()) / 1000));
    }
  }, []);

  useEffect(() => {
    if (!isLocked) return;
    const timer = setInterval(() => {
      const state = getLockout();
      const remaining = state.lockedUntil ? Math.ceil((state.lockedUntil - Date.now()) / 1000) : 0;
      if (remaining <= 0) {
        setIsLocked(false);
        setLockRemaining(0);
        saveLockout({ attempts: 0, lockedUntil: null });
      } else {
        setLockRemaining(remaining);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [isLocked]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isWaiting) return;

    const state = getLockout();
    if (state.lockedUntil && Date.now() < state.lockedUntil) {
      setIsLocked(true);
      setLockRemaining(Math.ceil((state.lockedUntil - Date.now()) / 1000));
      return;
    }

    if (password === ADMIN_PASSWORD) {
      saveLockout({ attempts: 0, lockedUntil: null });
      onLogin();
      return;
    }

    const newAttempts = state.attempts + 1;
    // 失敗ごとに待機時間を指数増加: 1s, 2s, 4s, 8s...
    const waitMs = Math.min(1000 * Math.pow(2, newAttempts - 1), 8000);

    if (newAttempts >= MAX_ATTEMPTS) {
      const lockedUntil = Date.now() + LOCKOUT_MS;
      saveLockout({ attempts: newAttempts, lockedUntil });
      setIsLocked(true);
      setLockRemaining(Math.ceil(LOCKOUT_MS / 1000));
      setError('');
    } else {
      saveLockout({ attempts: newAttempts, lockedUntil: null });
      setIsWaiting(true);
      setError(`認証に失敗しました（残り ${MAX_ATTEMPTS - newAttempts} 回）`);
      await new Promise(r => setTimeout(r, waitMs));
      setIsWaiting(false);
    }
    setPassword('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              Back to Home
            </Button>
          </div>
          <CardTitle className="text-2xl text-center">Admin Login</CardTitle>
        </CardHeader>
        <CardContent>
          {isLocked ? (
            <div className="text-center py-4">
              <div className="text-red-600 font-semibold mb-2">アカウントがロックされています</div>
              <div className="text-gray-500 text-sm">{lockRemaining}秒後に再試行できます</div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                  autoComplete="off"
                  disabled={isWaiting}
                />
              </div>
              {error && (
                <div className="text-red-600 text-sm text-center">{error}</div>
              )}
              <Button type="submit" className="w-full" disabled={isWaiting}>
                {isWaiting ? '確認中...' : 'Login'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const AdminDashboard = ({ onLogout }: { onLogout: () => void }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-sm text-gray-500">
              Measurement ID: {GA_PROPERTY_ID}
            </p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => navigate('/')} variant="outline" size="sm">
              Back to Home
            </Button>
            <Button onClick={onLogout} variant="outline" size="sm">
              Logout
            </Button>
          </div>
        </div>

        {/* GA4ダッシュボードへのリンク */}
        <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  Access Analytics with Google Analytics
                </h2>
                <p className="text-gray-600 mb-4">
                  View real-time access logs, regions, page views, and device information for all visitors.
                </p>
                <a
                  href={GA_DASHBOARD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Google Analytics
                  </Button>
                </a>
              </div>
              <div className="hidden sm:block">
                <BarChart3 className="h-20 w-20 text-blue-300" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Looker Studio レポート埋め込み */}
        <Card>
          <CardContent className="p-2">
            <iframe
              width="100%"
              height="600"
              src={LOOKER_STUDIO_EMBED_URL}
              frameBorder="0"
              style={{ border: 0 }}
              allowFullScreen
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  if (!isAuthenticated) {
    return <AdminLoginForm onLogin={() => setIsAuthenticated(true)} />;
  }

  return <AdminDashboard onLogout={() => setIsAuthenticated(false)} />;
};

export default AdminPage;
